// cells-sim-v0 reader: cells.json, events.json, spikes, the compartment table and the chunked
// `series/<quantity>/chunk-NNNN` arrays (`[frame][compartment]`, f32 or u8 with offset and scale).
// Chunks load only when a frame range needs them, and a trace decodes only its own compartments, so
// a long run never has to be resident as floats.

import type { SimFiles } from './files.ts';
import { axisFromHeader, duration, type TimeAxis } from './time.ts';
import type { CellEntry, CellEvent, CellsHeader, CellsSeriesInfo } from './types.ts';

const SOMA = 1;
const BASAL = 3;
const APICAL = 4;
const NONE = 0xffffffff;

const isQuantized = (info: CellsSeriesInfo) => /^[<|]?u(8|1)$/.test(info.dtype);

/** A timeline marker from the run's own record. Lower ranks claim label space first. */
export interface CellMarker {
  t: number;
  label: string;
  rank: number;
  kind: 'dendritic' | 'spike' | 'stimulus';
}

/** A dendritic compartment worth a trace: where one of the run's dendritic events peaked. */
export interface DendriteSite {
  compartment: number;
  type: 'basal' | 'apical' | 'dendrite';
  /** Path length from the soma along the tree, µm (straight-line distance when the run has no `morph/path.f32`). */
  distanceUm: number | null;
  /** 'Basal dendrite, 199 µm'. */
  label: string;
  /** The simulator's name for it, from events.json: `dend[84](0.75)`. */
  site?: string;
}

/** Dendritic events, by contract kind: the label for one, and for a count of them ('6 NMDA spikes'). */
const DENDRITIC: Record<string, [string, string]> = {
  'nmda-spike': ['NMDA spike', 'NMDA spikes'],
  'dendritic-na-spike': ['Dendritic Na⁺ spike', 'dendritic Na⁺ spikes'],
  'ca-spike': ['Ca²⁺ spike', 'Ca²⁺ spikes'],
};
const STIMULI: Record<string, [string, string]> = {
  'current-clamp': ['Current step', 'current steps'],
  'synaptic-cluster': ['Synaptic input', 'synaptic inputs'],
};
const SPIKE: [string, string] = ['Spike', 'spikes'];

/** Past this many of one kind, markers would crowd the transport: a circuit's raster carries them instead. */
const MAX_MARKERS = 24;
/** Traces drawn for dendritic sites, at most. */
export const MAX_SITES = 6;

/**
 * Markers for a set of times: one tick each, grouped where they fall within `win` of a group's first.
 * A group's first tick carries the label, with the count when there are several ('6 NMDA spikes');
 * the rest are ticks with no label of their own.
 */
export function groupMarkers(times: number[], [one, many]: [string, string], kind: CellMarker['kind'], rank: number, win: number): CellMarker[] {
  if (!times.length || times.length > MAX_MARKERS) return [];
  const sorted = [...times].sort((a, b) => a - b);
  const out: CellMarker[] = [];
  for (let i = 0; i < sorted.length; ) {
    let j = i + 1;
    while (j < sorted.length && sorted[j]! - sorted[i]! <= win) j++;
    const n = j - i;
    out.push({ t: sorted[i]!, label: n > 1 ? `${n} ${many}` : one, rank, kind });
    for (let k = i + 1; k < j; k++) if (sorted[k] !== sorted[k - 1]) out.push({ t: sorted[k]!, label: '', rank, kind });
    i = j;
  }
  return out;
}

export class CellsSim {
  readonly axis: TimeAxis;
  readonly compartments: number;
  private types: Promise<Uint8Array | null> | null = null;
  private paths: Promise<{ mm: Float32Array; measured: boolean } | null> | null = null;

  private constructor(
    readonly files: SimFiles,
    readonly header: CellsHeader,
    readonly cells: CellEntry[],
    /** events.json's events, or none when the run has no events file. */
    readonly events: CellEvent[],
    private readonly spikeData: { times: Float32Array; cell: Uint32Array } | null,
  ) {
    // Never periodic in this format: times outside the run clamp to its ends.
    this.axis = { ...axisFromHeader(header.time), periodic: false };
    this.compartments = header.counts?.compartments ?? cells.reduce((m, c) => Math.max(m, c.compartments.offset + c.compartments.count), 0);
  }

  static async open(files: SimFiles, header: CellsHeader): Promise<CellsSim> {
    const eventsFile = typeof header.files?.events === 'string' && header.files.events ? header.files.events : null;
    const [doc, events, spikes] = await Promise.all([
      files.json<{ cells: CellEntry[] }>(header.files?.cells ?? 'cells.json'),
      eventsFile ? files.json<{ events?: CellEvent[] }>(eventsFile).then((d) => (Array.isArray(d.events) ? d.events : [])) : Promise.resolve([]),
      Promise.all([files.raw('spikes/times_s.f32'), files.raw('spikes/cell.u32')]).then(
        ([times, cell]) => ({ times: times as Float32Array, cell: cell as Uint32Array }),
        () => null,
      ),
    ]);
    return new CellsSim(files, header, doc.cells, events.filter((e) => e && typeof e.t_on_s === 'number' && Number.isFinite(e.t_on_s)), spikes);
  }

  seriesInfo(quantity = 'v'): CellsSeriesInfo {
    const s = this.header.series?.find((x) => x.quantity === quantity);
    if (!s) throw new Error(`${this.header.id} has no ${quantity} series`);
    return s;
  }

  /** Frames held by chunk k: `[k·F, min((k+1)·F, frames))`. */
  chunkFrames(info: CellsSeriesInfo, k: number): [number, number] {
    const F = info.frames_per_chunk;
    return [k * F, Math.min((k + 1) * F, this.axis.frames)];
  }

  /** One chunk decoded to values, `[frame − k·F][compartment]`. */
  async chunk(quantity: string, k: number): Promise<Float32Array> {
    const info = this.seriesInfo(quantity);
    const path = info.chunks[k];
    if (!path) throw new Error(`${quantity}: no chunk ${k}`);
    const [f0, f1] = this.chunkFrames(info, k);
    const count = (f1 - f0) * this.compartments;
    const dtype = info.dtype.replace(/^[<|]/, '');
    if (dtype === 'u8' || dtype === 'u1') return (await this.files.raw(path, { count, offset: info.decode?.offset ?? 0, scale: info.decode?.scale ?? 1 })) as Float32Array;
    return (await this.files.raw(path, { count })) as Float32Array;
  }

  /** A chunk's values as stored (u8 codes or f32), checked against its frames × compartments. */
  private async chunkData(info: CellsSeriesInfo, k: number): Promise<{ data: Uint8Array | Float32Array; f0: number; f1: number }> {
    const path = info.chunks[k];
    if (!path) throw new Error(`${info.quantity}: no chunk ${k}`);
    const [f0, f1] = this.chunkFrames(info, k);
    const C = this.compartments;
    const quantized = isQuantized(info);
    const buf = await this.files.bytes(path);
    const want = (f1 - f0) * C * (quantized ? 1 : 4);
    if (buf.byteLength !== want) throw new Error(`${this.files.path(path)}: ${buf.byteLength} bytes, expected ${want} (${f1 - f0} frames × ${C} compartments)`);
    return { data: quantized ? new Uint8Array(buf) : new Float32Array(buf), f0, f1 };
  }

  /** Every compartment's value at frame f (clamped to the run): one row of its chunk, decoded. */
  async frame(quantity: string, f: number): Promise<Float32Array> {
    const info = this.seriesInfo(quantity);
    const i = Math.max(0, Math.min(this.axis.frames - 1, Math.round(f)));
    const { data, f0 } = await this.chunkData(info, Math.floor(i / info.frames_per_chunk));
    const C = this.compartments;
    const row = data.subarray((i - f0) * C, (i - f0 + 1) * C);
    if (!isQuantized(info)) return Float32Array.from(row);
    const offset = info.decode?.offset ?? 0;
    const scale = info.decode?.scale ?? 1;
    return Float32Array.from(row, (q) => offset + scale * q);
  }

  /** Compartment SWC types (`compartments/type.u8`), or null when the run has none. */
  compartmentTypes(): Promise<Uint8Array | null> {
    this.types ??= this.files.raw('compartments/type.u8', { count: this.compartments }).then(
      (t) => t as Uint8Array,
      () => null,
    );
    return this.types;
  }

  /** The compartment a cell's soma voltage is read from: its first soma-typed compartment, else its first. */
  async somaCompartment(cell: CellEntry): Promise<number> {
    const { offset, count } = cell.compartments;
    if (cell.recording === 'soma' || count === 1) return offset;
    const types = await this.compartmentTypes();
    if (types) for (let c = offset; c < offset + count; c++) if (types[c] === SOMA) return c;
    return offset;
  }

  /**
   * Each compartment's distance from its cell's soma, mm: the mean path length of the vertices it
   * colours (`morph/path.f32`), else the straight line between compartment centres. Null when the run
   * has neither.
   */
  private compartmentDistances(): Promise<{ mm: Float32Array; measured: boolean } | null> {
    this.paths ??= (async () => {
      const C = this.compartments;
      const path = await Promise.all([this.files.raw('morph/path.f32'), this.files.raw('morph/vertex_compartment.u32')]).catch(() => null);
      if (path) {
        const [p, vc] = path as [Float32Array, Uint32Array];
        if (p.length !== vc.length) return null;
        const sum = new Float64Array(C);
        const n = new Uint32Array(C);
        for (let v = 0; v < vc.length; v++) {
          const c = vc[v]!;
          if (c === NONE || c >= C) continue;
          sum[c] = sum[c]! + p[v]!;
          n[c] = n[c]! + 1;
        }
        return { mm: Float32Array.from(sum, (s, c) => (n[c] ? s / n[c]! : NaN)), measured: true };
      }
      const centre = (await this.files.raw('compartments/centre.f32', { count: C * 3 }).catch(() => null)) as Float32Array | null;
      if (!centre) return null;
      const mm = new Float32Array(C).fill(NaN);
      for (const cell of this.cells) {
        const s = await this.somaCompartment(cell);
        for (let c = cell.compartments.offset; c < cell.compartments.offset + cell.compartments.count; c++)
          mm[c] = Math.hypot(centre[c * 3]! - centre[s * 3]!, centre[c * 3 + 1]! - centre[s * 3 + 1]!, centre[c * 3 + 2]! - centre[s * 3 + 2]!);
      }
      return { mm, measured: false };
    })();
    return this.paths;
  }

  /**
   * The dendritic sites to trace for a cell: where its dendritic events (NMDA, Na⁺ or Ca²⁺ spikes)
   * peaked, in the order they first fire, one per compartment and per named site.
   */
  async dendriteSites(cell: CellEntry, max = MAX_SITES): Promise<DendriteSite[]> {
    const picks: { compartment: number; site?: string }[] = [];
    const lo = cell.compartments.offset;
    const hi = lo + cell.compartments.count;
    const events = this.events.filter((e) => DENDRITIC[e.kind] && e.cell === cell.index).sort((a, b) => a.t_on_s - b.t_on_s);
    for (const e of events) {
      const c = e.peak_compartment ?? e.compartments?.[0];
      if (c === undefined || c < lo || c >= hi) continue;
      if (picks.some((p) => p.compartment === c || (e.site && p.site === e.site))) continue;
      picks.push({ compartment: c, ...(e.site ? { site: e.site } : {}) });
      if (picks.length >= max) break;
    }
    if (!picks.length) return [];
    const [types, dist] = await Promise.all([this.compartmentTypes(), this.compartmentDistances()]);
    return picks.map(({ compartment, site }) => {
      const t = types?.[compartment];
      const type = t === BASAL ? 'basal' : t === APICAL ? 'apical' : 'dendrite';
      const mm = dist?.mm[compartment];
      const distanceUm = mm !== undefined && Number.isFinite(mm) ? mm * 1000 : null;
      const name = type === 'basal' ? 'Basal dendrite' : type === 'apical' ? 'Apical dendrite' : 'Dendrite';
      // A thin space between number and unit (design system v1, copy.units).
      return { compartment, type, distanceUm, label: distanceUm === null ? name : `${name}, ${Math.round(distanceUm)}\u2009µm`, ...(site ? { site } : {}) };
    });
  }

  /**
   * Values of some compartments over frames [f0, f1), one Float32Array per compartment. Only those
   * columns are decoded, chunk by chunk, so a whole-run trace never holds a run's worth of floats.
   */
  async traces(quantity: string, compartments: number[], f0 = 0, f1 = this.axis.frames): Promise<Float32Array[]> {
    const info = this.seriesInfo(quantity);
    const C = this.compartments;
    const out = compartments.map(() => new Float32Array(Math.max(0, f1 - f0)));
    const F = info.frames_per_chunk;
    const quantized = isQuantized(info);
    const offset = info.decode?.offset ?? 0;
    const scale = info.decode?.scale ?? 1;
    const ks: number[] = [];
    for (let k = Math.floor(f0 / F); k * F < f1 && k < info.chunks.length; k++) ks.push(k);
    await Promise.all(
      ks.map(async (k) => {
        const { data, f0: c0, f1: c1 } = await this.chunkData(info, k);
        const a = Math.max(c0, f0);
        const b = Math.min(c1, f1);
        compartments.forEach((c, j) => {
          const col = out[j]!;
          for (let f = a; f < b; f++) {
            const q = data[(f - c0) * C + c]!;
            col[f - f0] = quantized ? offset + scale * q : q;
          }
        });
      }),
    );
    return out;
  }

  /** Somatic spikes, sorted by time. */
  async spikes(): Promise<{ times: Float32Array; cell: Uint32Array } | null> {
    return this.spikeData;
  }

  /**
   * A cell's arbor from `morph/`: its dendrites' cable, mm (the segments with both ends on a basal or
   * apical dendrite, which is NEURON's dendritic L), and its reach, the farthest vertex from its soma.
   * Null when the run has no morphology or the cell no segments.
   */
  async arbor(cell: CellEntry): Promise<{ dendritesMm: number; reachMm: number } | null> {
    const seg = cell.segments;
    if (!seg?.count) return null;
    const morph = await Promise.all([this.files.raw('morph/positions.f32'), this.files.raw('morph/segments.u32'), this.files.raw('morph/vertex_type.u8')]).catch(() => null);
    if (!morph) return null;
    const [p, ends, type] = morph as [Float32Array, Uint32Array, Uint8Array];
    const dendrite = (v: number) => type[v] === BASAL || type[v] === APICAL;
    let dendritesMm = 0;
    for (let s = seg.offset; s < seg.offset + seg.count; s++) {
      const a = ends[2 * s]!;
      const b = ends[2 * s + 1]!;
      if (dendrite(a) && dendrite(b)) dendritesMm += Math.hypot(p[3 * b]! - p[3 * a]!, p[3 * b + 1]! - p[3 * a + 1]!, p[3 * b + 2]! - p[3 * a + 2]!);
    }
    const soma = cell.soma_xyz ?? [0, 0, 0];
    let reach = 0;
    const v = cell.vertices ?? { offset: 0, count: p.length / 3 };
    for (let i = v.offset; i < v.offset + v.count; i++) reach = Math.max(reach, Math.hypot(p[3 * i]! - soma[0], p[3 * i + 1]! - soma[1], p[3 * i + 2]! - soma[2]));
    return { dendritesMm, reachMm: reach };
  }

  /**
   * Timeline markers from the run's own record: its dendritic events and somatic spikes, and its
   * stimuli (events.json's, else the header's `scenario.stimuli`). Dendritic events claim label space
   * first, then spikes, then the stimuli that caused them.
   */
  markers(): CellMarker[] {
    const end = this.axis.frames > 1 ? duration(this.axis) + this.axis.t0 : this.axis.t0;
    const win = Math.max(0.002, (end - this.axis.t0) * 0.02);
    const inRun = (t: number) => Number.isFinite(t) && t >= this.axis.t0 - 1e-9 && t <= end + 1e-9;
    const out: CellMarker[] = [];
    for (const [kind, names] of Object.entries(DENDRITIC))
      out.push(...groupMarkers(this.events.filter((e) => e.kind === kind && inRun(e.t_on_s)).map((e) => e.t_on_s), names, 'dendritic', 0, win));
    if (this.spikeData) out.push(...groupMarkers(Array.from(this.spikeData.times).filter(inRun), SPIKE, 'spike', 1, win));
    const stimuli = this.events.filter((e) => e.kind === 'stimulus');
    const listed = stimuli.length
      ? stimuli.map((e) => ({ type: e.stimulus ?? 'stimulus', t: e.t_on_s }))
      : (this.header.scenario?.stimuli ?? []).map((s) => ({ type: s.type ?? s.kind ?? 'stimulus', t: s.t_on_s ?? s.t_s ?? s.t }));
    const byType = new Map<string, number[]>();
    for (const s of listed) if (typeof s.t === 'number' && inRun(s.t)) byType.set(s.type, [...(byType.get(s.type) ?? []), s.t]);
    for (const [type, times] of byType) out.push(...groupMarkers(times, STIMULI[type] ?? ['Stimulus', 'stimuli'], 'stimulus', 2, win));
    return out.sort((a, b) => a.t - b.t || a.rank - b.rank);
  }
}
